/* Puravida Lash — Landing v2 analytics module.
 *
 * Sends Web Vitals (LCP, CLS, INP, FID, TTFB) and custom landing events
 * to GA4 via window.gtag (which Shopify auto-injects when GA4 is configured
 * in the store admin). All events are de-duplicated and gracefully no-op if
 * gtag is unavailable. Fallback log to window.__pvlAnalytics for debug.
 *
 * Custom events:
 *   - pvl_act_view {act_name}            fires once per act when ≥50% visible
 *   - pvl_cta_click {label, target, origin}  on primary CTA clicks
 *   - pvl_scroll_depth {percent}         at 25/50/75/100% milestones
 *   - web_vitals {metric_name, value, rating}  on each Vitals measurement
 *
 * Loaded after web-vitals.iife.js (which exposes window.webVitals).
 */
(function () {
  'use strict';

  var sentEvents = new Set();
  var pendingQueue = [];
  var gtagFlushed = false;
  var flushAttempts = 0;
  var MAX_FLUSH_ATTEMPTS = 20; // 20 × 500ms = 10s window for gtag to load

  function deliver(name, params) {
    if (typeof window.gtag === 'function') {
      try { window.gtag('event', name, params); return true; } catch (e) {}
    }
    return false;
  }

  function tryFlushQueue() {
    if (gtagFlushed) return;
    if (typeof window.gtag !== 'function') {
      flushAttempts++;
      if (flushAttempts < MAX_FLUSH_ATTEMPTS) {
        setTimeout(tryFlushQueue, 500);
      }
      return;
    }
    gtagFlushed = true;
    while (pendingQueue.length) {
      var e = pendingQueue.shift();
      deliver(e.name, e.params);
    }
  }

  function send(name, params) {
    var key = name + ':' + (params.metric_name || params.act_name || params.percent || params.cta_label || '');
    if (sentEvents.has(key)) return;
    sentEvents.add(key);
    (window.__pvlAnalytics = window.__pvlAnalytics || []).push({
      name: name, params: params, ts: Date.now(),
    });
    if (gtagFlushed && deliver(name, params)) return;
    pendingQueue.push({ name: name, params: params });
    if (!gtagFlushed) tryFlushQueue();
  }

  function setupWebVitals() {
    if (!window.webVitals) {
      setTimeout(setupWebVitals, 250);
      return;
    }
    var v = window.webVitals;
    function report(metricName, formatter) {
      return function (m) {
        send('web_vitals', {
          metric_name: metricName,
          value: formatter ? formatter(m.value) : Math.round(m.value),
          rating: m.rating,
          delta: m.delta,
        });
      };
    }
    v.onLCP && v.onLCP(report('LCP'));
    v.onCLS && v.onCLS(report('CLS', function (x) { return +x.toFixed(4); }));
    v.onINP && v.onINP(report('INP'));
    v.onFID && v.onFID(report('FID'));
    v.onTTFB && v.onTTFB(report('TTFB'));
  }

  function setupActTracking() {
    if (!('IntersectionObserver' in window)) return;
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
          var act = entry.target.dataset.pvlAct;
          if (act) {
            send('pvl_act_view', { act_name: act });
          }
        }
      });
    }, { threshold: [0.5] });
    document.querySelectorAll('[data-pvl-act]').forEach(function (el) {
      observer.observe(el);
    });
  }

  function setupCTATracking() {
    var ctaSelector = [
      '.pvl-a1__cta',
      '.pvl-a2__cta',
      '.pvl-a3__cta',
      '.pvl-a4__cta',
      '.pvl-a5__cta',
      '.pvl-a6__cta',
      '.pvl-a7__btn',
      '[data-pvl-cta]',
    ].join(',');

    document.addEventListener('click', function (event) {
      var cta = event.target.closest(ctaSelector);
      if (!cta) return;
      var act = cta.closest('[data-pvl-act]');
      send('pvl_cta_click', {
        cta_label: (cta.textContent || '').trim().slice(0, 60),
        cta_target: cta.getAttribute('href') || '',
        act_origin: act ? act.dataset.pvlAct : 'unknown',
      });
    }, { passive: true });
  }

  function setupScrollDepth() {
    var milestones = [25, 50, 75, 100];
    var fired = new Set();
    var ticking = false;

    function check() {
      var doc = document.documentElement;
      var max = doc.scrollHeight - window.innerHeight;
      if (max <= 0) return;
      var pct = Math.round((window.scrollY / max) * 100);
      milestones.forEach(function (m) {
        if (pct >= m && !fired.has(m)) {
          fired.add(m);
          send('pvl_scroll_depth', { percent: m });
        }
      });
    }

    window.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        check();
        ticking = false;
      });
    }, { passive: true });
  }

  function init() {
    try { setupActTracking(); } catch (e) {}
    try { setupCTATracking(); } catch (e) {}
    try { setupScrollDepth(); } catch (e) {}
    try { setupWebVitals(); } catch (e) {}
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
